import api from './api';

// ============================================
// ContactRequestService – Port des Flutter ContactRequestService
// Ticket-/Chat-System für Kontaktanfragen (User-Sicht).
//   POST   /api/contact-request                  {category, subject, message}
//   GET    /api/contact-requests                 -> ContactRequest[]
//   GET    /api/contact-request/:id              -> ContactRequest
//   POST   /api/contact-request/:id/message      {content}
//   POST   /api/contact-request/:id/read         {}
//   GET    /api/contact-requests/unread-count    -> { unreadCount }
// Alle defensiv geparst.
// ============================================

export type ContactStatus = 'open' | 'in_progress' | 'closed' | string;
export type ContactCategory = 'general' | 'technical' | 'feedback' | 'partnership' | 'other' | string;

export interface ContactMessage {
  id: string;
  senderId: string;
  senderName: string;
  senderType: 'user' | 'admin' | string;
  content: string;
  timestamp: number;
  read: boolean;
  readAt?: number | null;
}

export interface ContactRequest {
  id: string;
  createdAt: number;
  updatedAt?: number | null;
  closedAt?: number | null;
  userId: string;
  userName: string;
  userEmail: string;
  category: ContactCategory;
  subject: string;
  status: ContactStatus;
  messages: ContactMessage[];
  lastMessage?: string | null;
  lastMessageSenderType?: string | null;
  lastMessageTime?: number | null;
  unreadCountUser: number;
  unreadCountAdmin: number;
  isActive: boolean;
}

export const CATEGORY_OPTIONS: { value: ContactCategory; label: string }[] = [
  { value: 'general', label: 'Allgemein' },
  { value: 'technical', label: 'Technisches Problem' },
  { value: 'feedback', label: 'Feedback' },
  { value: 'partnership', label: 'Partnerschaft' },
  { value: 'other', label: 'Sonstiges' },
];

export const categoryLabel = (category?: string): string =>
  CATEGORY_OPTIONS.find((c) => c.value === category)?.label || category || 'Allgemein';

export const statusLabel = (status?: string): string => {
  switch (status) {
    case 'open':
      return 'Offen';
    case 'in_progress':
      return 'In Bearbeitung';
    case 'closed':
      return 'Geschlossen';
    default:
      return status || '';
  }
};

const parseMessage = (m: any): ContactMessage => ({
  id: m?.id ?? '',
  senderId: m?.senderId ?? '',
  senderName: m?.senderName ?? 'Unbekannt',
  senderType: m?.senderType ?? 'user',
  content: m?.content ?? '',
  timestamp: m?.timestamp ?? 0,
  read: m?.read ?? false,
  readAt: m?.readAt ?? null,
});

const parseRequest = (d: any): ContactRequest | null => {
  if (!d || typeof d !== 'object') return null;
  return {
    id: d.id ?? d._id ?? '',
    createdAt: d.createdAt ?? 0,
    updatedAt: d.updatedAt ?? null,
    closedAt: d.closedAt ?? null,
    userId: d.userId ?? '',
    userName: d.userName ?? '',
    userEmail: d.userEmail ?? '',
    category: d.category ?? 'general',
    subject: d.subject ?? '',
    status: d.status ?? 'open',
    messages: Array.isArray(d.messages) ? d.messages.map(parseMessage) : [],
    lastMessage: d.lastMessage ?? null,
    lastMessageSenderType: d.lastMessageSenderType ?? null,
    lastMessageTime: d.lastMessageTime ?? null,
    unreadCountUser: d.unreadCountUser ?? 0,
    unreadCountAdmin: d.unreadCountAdmin ?? 0,
    isActive: d.isActive ?? true,
  };
};

class ContactRequestService {
  async getMyRequests(): Promise<ContactRequest[]> {
    try {
      const res = await api.get('/api/contact-requests');
      const list = Array.isArray(res.data) ? res.data : res.data?.requests;
      if (!Array.isArray(list)) return [];
      return list.map(parseRequest).filter((r): r is ContactRequest => r !== null);
    } catch {
      return [];
    }
  }

  async createRequest(payload: {
    category: string;
    subject: string;
    message: string;
  }): Promise<ContactRequest | null> {
    const res = await api.post('/api/contact-request', payload);
    return parseRequest(res.data);
  }

  async getRequestById(id: string): Promise<ContactRequest | null> {
    try {
      const res = await api.get(`/api/contact-request/${encodeURIComponent(id)}`);
      return parseRequest(res.data);
    } catch {
      return null;
    }
  }

  async sendMessage(id: string, content: string): Promise<ContactRequest | null> {
    const res = await api.post(`/api/contact-request/${encodeURIComponent(id)}/message`, {
      content,
    });
    return parseRequest(res.data);
  }

  async markAsRead(id: string): Promise<ContactRequest | null> {
    try {
      const res = await api.post(`/api/contact-request/${encodeURIComponent(id)}/read`, {});
      return parseRequest(res.data);
    } catch {
      return null;
    }
  }

  async getUnreadCount(): Promise<number> {
    try {
      const res = await api.get('/api/contact-requests/unread-count');
      return res.data?.unreadCount ?? 0;
    } catch {
      return 0;
    }
  }
}

export default new ContactRequestService();
